import { setDoc, updateDoc, type DocumentReference } from "firebase/firestore";

export type ParentNursingSyncPayload = {
  submitted: boolean[];
  crossAuditByDeptIds: string[];
};

export type ParentNursingSyncTelemetryEvent =
  | { context: string; outcome: "updated" }
  | { context: string; outcome: "created-merge" }
  | { context: string; outcome: "failed"; phase: "update" | "setDoc-fallback"; err: unknown };

export const getFirestoreErrorCode = (err: unknown): string =>
  err && typeof err === "object" && "code" in err ? String((err as { code: unknown }).code) : "";

/** True when Firestore reports the target document path does not exist (updateDoc would fail). */
export const isFirestoreDocumentNotFoundError = (err: unknown): boolean => {
  const c = getFirestoreErrorCode(err);
  return c === "not-found" || c === "firestore/not-found";
};

type ParentNursingSyncDeps = {
  updateDocImpl?: typeof updateDoc;
  setDocImpl?: typeof setDoc;
};

/**
 * Writes `submitted` / `crossAuditByDeptIds` onto parent nurse_notes docs after a cross-audit slice submit.
 * Uses updateDoc when the doc exists; otherwise setDoc(..., { merge: true }) so first-time cross-audit-only
 * submits still persist flags for hydration. `deps` overrides Firestore calls (unit tests).
 */
export async function syncParentNursingSubmittedFields(
  ref: DocumentReference,
  syncPayload: ParentNursingSyncPayload,
  context: string,
  logParentSyncTelemetry: (event: ParentNursingSyncTelemetryEvent) => void,
  deps?: ParentNursingSyncDeps,
): Promise<void> {
  const doUpdate = deps?.updateDocImpl ?? updateDoc;
  const doSet = deps?.setDocImpl ?? setDoc;
  try {
    await doUpdate(ref, syncPayload);
    logParentSyncTelemetry({ context, outcome: "updated" });
  } catch (err) {
    if (!isFirestoreDocumentNotFoundError(err)) {
      logParentSyncTelemetry({ context, outcome: "failed", phase: "update", err });
      return;
    }
    try {
      await doSet(ref, syncPayload, { merge: true });
      logParentSyncTelemetry({ context, outcome: "created-merge" });
    } catch (fallbackErr) {
      logParentSyncTelemetry({
        context,
        outcome: "failed",
        phase: "setDoc-fallback",
        err: fallbackErr,
      });
    }
  }
}
